import React, {ReactNode} from 'react';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {UnreachableError} from '../../../../../libs/shared/helpers/src/types/customErrors';
import {Icon, IconName} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {InboxesFiltersEnum, InboxesSearchLocationsEnum} from '../../../../models/url/models/inboxesUrlModel';
import {InboxesParameters} from '../../../../store/panels/inboxes/parameters/inboxesParameters';
import {
  SearchLocalFilterIconType,
  SearchLocalFilterTypesEnum,
} from '../../../../store/panels/inboxes/parameters/inboxesParametersFiltersIconHelpers';
import {TeamAvatar} from '../../../common/avatars/teamAvatar';
import {TeammateAvatar} from '../../../common/avatars/teammateAvatar';
import {ColorDot} from '../../../common/colorDot';
import {Emoji} from '../../../common/emoji/emoji';
import {SegmentedFilters} from '../../../common/segmentedFilters/segmentedFilters';
import {SegmentedFiltersPaginated} from '../../../common/segmentedFilters/segmentedFiltersPaginated';

/*
 * Props.
 */

export type Filter = InboxesFiltersEnum | InboxesSearchLocationsEnum;
export type OnFilterSelectOption = {
  shouldPersistSelectedFilter: boolean;
};
export type OnFilterSelectHandler<T extends Filter = Filter> = (
  filter: T,
  options: OnFilterSelectOption,
) => void;
export type OnMultipleFiltersSelectHandler<T extends Filter = Filter> = (filters: ReadonlyArray<T>) => void;
export interface CellsHeaderFiltersProps<T extends Filter = Filter> {
  parameters?: InboxesParameters;
  filters: ReadonlyArray<T>;
  selectedFilter: T;
  selectedFilters: ReadonlyArray<T>;
  localSearchFilterName?: ReactNode;
  localSearchFilterIcon?: SearchLocalFilterIconType;
  onFilterSelect?: OnFilterSelectHandler<T>;
  onMultipleFiltersSelect?: OnMultipleFiltersSelectHandler<T>;
  canUseMultipleFilters?: boolean;
  searchQuery?: string;
  trackCurrentSearch?: HandlerOf<boolean>;
}

/*
 * Component.
 */

export const CellsHeaderFilters = <T extends Filter = Filter>(props: CellsHeaderFiltersProps<T>) => {
  const theme = useUserTheme();
  const {
    parameters,
    filters,
    selectedFilter,
    selectedFilters,

    searchQuery,
  } = props;

  const selectedFilterIndex = filters.indexOf(selectedFilter);
  const isInSearch = Boolean(props.localSearchFilterName);
  const color = isInSearch ? theme.greys.shade70 : theme.interactiveText;

  const filterDefinitions = makeFilterDefinitions(props);

  if (props.canUseMultipleFilters && !isInSearch && parameters) {
    return (
      <SegmentedFiltersPaginated
        parameters={parameters}
        filters={filterDefinitions}
        selectedFilters={selectedFilters}
      />
    );
  }

  return (
    <SegmentedFilters
      filters={filterDefinitions}
      selectedFilterIndex={selectedFilterIndex}
      color={color}
      searchQuery={searchQuery}
    />
  );
};

type FilterNames = {[T in Filter]?: ReactNode};
const filterNames: FilterNames = {
  [InboxesFiltersEnum.UNASSIGNED]: <Trans id="cell-filter-unassigned">Unassigned</Trans>,
  [InboxesFiltersEnum.OPEN]: <Trans id="cell-filter-open">Open</Trans>,
  [InboxesFiltersEnum.ALL]: <Trans id="cell-filter-all">All</Trans>,
  [InboxesFiltersEnum.ASSIGNED]: <Trans id="cell-filter-assigned">Assigned</Trans>,
  [InboxesFiltersEnum.SNOOZED]: <Trans id="cell-filter-snoozed">Snoozed</Trans>,
  [InboxesFiltersEnum.TRASHED]: <Trans id="cell-filter-trash">Trash</Trans>,
  [InboxesFiltersEnum.SPAM]: <Trans id="cell-filter-spam">Spam</Trans>,
  [InboxesFiltersEnum.ARCHIVED]: <Trans id="cell-filter-archived">Archived</Trans>,
  [InboxesSearchLocationsEnum.GLOBAL]: <Trans id="cell-filter-allinboxes">All inboxes</Trans>,
  [InboxesSearchLocationsEnum.PINNED_IN_WORKSPACE]: <Trans id="cell-filter-sidebar">My sidebar</Trans>,
};

/** Render the name of the specified filter. */
export function renderFilterName<T extends Filter>(filter: T, localSearchFilterName?: ReactNode) {
  // If this is a local search filter, use the parameters to render the name.
  if (filter === InboxesSearchLocationsEnum.LOCAL) {
    return localSearchFilterName || null;
  }

  // Otherwise, use the localization map.
  return filterNames[filter] || null;
}

type FilterIcons = {[T in Filter]?: IconName};
const filterIcons: FilterIcons = {
  [InboxesSearchLocationsEnum.PINNED_IN_WORKSPACE]: 'workspaceSmall',
  [InboxesSearchLocationsEnum.GLOBAL]: 'inboxSmall',
};

/** Render the icon of the specified filter. */
export function renderFilterIcon<T extends Filter>(
  filter: T,
  localSearchFilterIcon: SearchLocalFilterIconType,
): ReactNode {
  // If this is a local search filter, use the parameters to render the icon.
  if (filter === InboxesSearchLocationsEnum.LOCAL) {
    return renderLocalFilterIcon(localSearchFilterIcon);
  }

  // Otherwise, use the icon map.
  const iconName = filterIcons[filter];
  if (!iconName) {
    return null;
  }

  return (
    <UserThemeConsumer>{(theme) => <Icon name={iconName} color={theme.greys.shade70} />}</UserThemeConsumer>
  );
}

export function renderLocalFilterIcon(icon: SearchLocalFilterIconType) {
  switch (icon.type) {
    case SearchLocalFilterTypesEnum.EMOJI:
      return <Emoji emoji={icon.emoji.emoji} skinTone={icon.emoji.skinTone} size={16} />;

    case SearchLocalFilterTypesEnum.ICON:
      return (
        <UserThemeConsumer>
          {(theme) => <Icon name={icon.iconName} color={icon.color ?? theme.greys.shade70} />}
        </UserThemeConsumer>
      );

    case SearchLocalFilterTypesEnum.DOT: {
      return (
        <UserThemeConsumer>
          {(theme) => (
            <ColorDot
              size={8}
              color={icon.color || theme.greys.shade40}
              hoverColor={icon.color || theme.greys.shade50}
            />
          )}
        </UserThemeConsumer>
      );
    }

    case SearchLocalFilterTypesEnum.TEAM:
      return <TeamAvatar team={icon.team} size={VisualSizesEnum.SMALL} />;

    case SearchLocalFilterTypesEnum.TEAMMATE:
      return <TeammateAvatar teammate={icon.teammate} size={VisualSizesEnum.SMALL} />;

    default:
      throw new UnreachableError(icon);
  }
}

type FilterTooltips = {[T in Filter]?: ReactNode};
export const filterTooltips: FilterTooltips = {
  [InboxesSearchLocationsEnum.PINNED_IN_WORKSPACE]: (
    <Trans id="search-filter-sidebar-tooltip">
      Show me results from inboxes, tags, views, and teammates that I&apos;ve added to my sidebar.
    </Trans>
  ),
  [InboxesSearchLocationsEnum.GLOBAL]: (
    <Trans id="search-filter-all-inboxes-tooltip">
      Show me search results from all inboxes, tags, views, and teammates that I have access to.
    </Trans>
  ),
};

type FilterTooltipsPreventPinTeamTags = {[T in Filter]?: ReactNode};
export const filterTooltipsPreventPinTeamTags: FilterTooltipsPreventPinTeamTags = {
  [InboxesSearchLocationsEnum.PINNED_IN_WORKSPACE]: (
    <Trans id="search-filter-sidebar-tooltip-prevent-pin-team-tags">
      Show me results from inboxes, private tags, and views that I&apos;ve added to my sidebar.
    </Trans>
  ),
  [InboxesSearchLocationsEnum.GLOBAL]: (
    <Trans id="search-filter-all-inboxes-tooltip-prevent-pin-team-tags">
      Show me search results from all inboxes, private tags, and views that I have access to.
    </Trans>
  ),
};

export interface PaginatedFilterDefinition {
  id: Filter;
  content: ReactNode;
  onMultipleFiltersSelect?: HandlerOf<ReadonlyArray<Filter>>;
  onClick: (shouldPersistSelectedFilter?: boolean) => void;
}

export function makeFilterDefinitions<T extends Filter = Filter>(
  props: CellsHeaderFiltersProps<T>,
): ReadonlyArray<PaginatedFilterDefinition> {
  return props.filters.map((filter, index) => ({
    id: filter,
    content: renderFilterName(filter, props.localSearchFilterName),
    onClick: (shouldPersistSelectedFilter: boolean = true) => {
      if (!props.onFilterSelect) {
        return;
      }

      props.trackCurrentSearch?.(true);

      props.onFilterSelect(filter, {shouldPersistSelectedFilter});
    },
    onMultipleFiltersSelect: (newFilters: ReadonlyArray<Filter>) => {
      props.onMultipleFiltersSelect?.(newFilters as ReadonlyArray<T>);
    },
  }));
}
